import {
  getNiraAppointmentTypeFromBookedEhrId,
  getNiraBookedTypeIdFromOpenTypeId,
} from "@/configuration/groups/nira/configuration";
import {
  getSportOrthoAppointmentTypeFromBookedEhrId,
  getSportOrthoBookedTypeIdFromOpenTypeId,
} from "@/configuration/groups/sportortho/configuration";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { AppointmentType } from "@/src/models/appointment";
import { Group } from "@/src/models/group";

export const DEFAULT_CASE_SUBJECT = "Patient Referral";

export const REFERRAL_SOURCE_IDS = {
  PCP: "2",
  SPECIALIST: "41",
  PHYSICIAN_REFERRAL: "3",
};

export const REFERRAL_SOURCE_IDS_LABELS = {
  [REFERRAL_SOURCE_IDS.PCP]: "PCP",
  [REFERRAL_SOURCE_IDS.SPECIALIST]: "Specialist",
  [REFERRAL_SOURCE_IDS.PHYSICIAN_REFERRAL]: "Physician Referral",
};

export const UPPERLINE_GROUP_NAMES = [
  "Upperline",
  "Upperline Georgia",
  "Upperline Indiana",
  "Upperline North Florida",
  "Upperline Orlando",
  "Upperline Tennessee",
];

export const NIRA_GROUP_NAMES = ["Nira"];

export const SPORTORTHO_GROUP_NAMES = ["SportOrtho"];

export const ATHENA_GROUP_NAMES = [
  "HVC",
  "Qoros",
  "Nira",
  "Qoros CFCG",
  "Nimbus",
  "StrideCare",
  "ConvenientMD",
  ...UPPERLINE_GROUP_NAMES,
  ...SPORTORTHO_GROUP_NAMES,
];

const ATHENA_EHR_CONFIG_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.HVC,
  ConfigSubdomain.QOROS_BC,
  ConfigSubdomain.QOROS_CFCG,
  ConfigSubdomain.NIRA,
  ConfigSubdomain.NIMBUS,
  ConfigSubdomain.STRIDECARE,
  ConfigSubdomain.CONVENIENT_MD,
  ConfigSubdomain.USRC_CPS_AK,
  ConfigSubdomain.USRC_CPS_CO,
  ConfigSubdomain.UPPERLINE,
  ConfigSubdomain.UPPERLINE_GEORGIA,
  ConfigSubdomain.UPPERLINE_INDIANA,
  ConfigSubdomain.UPPERLINE_NORTH_FLORIDA,
  ConfigSubdomain.UPPERLINE_ORLANDO,
  ConfigSubdomain.UPPERLINE_TENNESSEE,
  ConfigSubdomain.SPORTORTHO,
]);

export function isAthenaEhrGroup(group: Group): boolean {
  return ATHENA_EHR_CONFIG_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

export function isNiraEhrGroup(group: Group): boolean {
  return getGroupConfigSubdomain(group) === ConfigSubdomain.NIRA;
}

export function isSportOrthoEhrGroup(group: Group): boolean {
  return getGroupConfigSubdomain(group) === ConfigSubdomain.SPORTORTHO;
}

const UPPERLINE_EHR_CONFIG_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.UPPERLINE,
  ConfigSubdomain.UPPERLINE_GEORGIA,
  ConfigSubdomain.UPPERLINE_INDIANA,
  ConfigSubdomain.UPPERLINE_NORTH_FLORIDA,
  ConfigSubdomain.UPPERLINE_ORLANDO,
  ConfigSubdomain.UPPERLINE_TENNESSEE,
]);

export function isUpperlineEhrGroup(group: Group): boolean {
  return UPPERLINE_EHR_CONFIG_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

type BookedAppointmentTypeResolver = (providerEhrId: string, appointmentTypeId: number) => AppointmentType | null;

// Register per-group resolvers that translate Athena's booked-appointment
// `appointmenttypeid` into our internal AppointmentType. Groups absent from the
// registry fall back to string-keyword matching in athena-types.
const BOOKED_APPOINTMENT_TYPE_RESOLVERS: Partial<Record<ConfigSubdomain, BookedAppointmentTypeResolver>> = {
  [ConfigSubdomain.NIRA]: getNiraAppointmentTypeFromBookedEhrId,
  [ConfigSubdomain.SPORTORTHO]: getSportOrthoAppointmentTypeFromBookedEhrId,
};

export function hasBookedAppointmentTypeMapping(group: Group): boolean {
  return BOOKED_APPOINTMENT_TYPE_RESOLVERS[getGroupConfigSubdomain(group) as ConfigSubdomain] !== undefined;
}

export function getAppointmentTypeFromBookedEhrId(
  group: Group,
  providerEhrId: string,
  appointmentTypeId: number
): AppointmentType | null {
  const resolver = BOOKED_APPOINTMENT_TYPE_RESOLVERS[getGroupConfigSubdomain(group) as ConfigSubdomain];

  return resolver?.(providerEhrId, appointmentTypeId) ?? null;
}

type OpenToBookedTypeIdResolver = (
  providerEhrId: string,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
) => number;

// Per-group resolvers that translate an open appointment-type id (carried on
// each available slot from the EHR) into the booked appointment-type id the
// clinic wants for that slot. Some open templates are reused by the clinic
// across multiple booked categories, so the internal AppointmentType
// is also passed through to disambiguate. Groups absent from the registry
// fall back to the legacy internal-type mapping in getAppointmentTypeId.
const OPEN_TO_BOOKED_TYPE_ID_RESOLVERS: Partial<Record<ConfigSubdomain, OpenToBookedTypeIdResolver>> = {
  [ConfigSubdomain.NIRA]: getNiraBookedTypeIdFromOpenTypeId,
  [ConfigSubdomain.SPORTORTHO]: getSportOrthoBookedTypeIdFromOpenTypeId,
};

export function getBookedTypeIdFromOpenTypeId(
  group: Group,
  providerEhrId: string,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number | null {
  const resolver = OPEN_TO_BOOKED_TYPE_ID_RESOLVERS[getGroupConfigSubdomain(group) as ConfigSubdomain];

  return resolver?.(providerEhrId, openAppointmentTypeId, internalAppointmentType) ?? null;
}
